import { alpha, createTheme, responsiveFontSizes } from '@mui/material/styles';

export const brand = {
  primary: '#4F46E5',
  primaryLight: '#818CF8',
  primaryDark: '#3730A3',
  violet: '#7C3AED',
  pink: '#EC4899',
  orange: '#F97316',
  green: '#10B981',
  ink: '#0F172A',
  slate: '#475569',
  muted: '#94A3B8',
  border: '#E2E8F0',
  surface: '#FFFFFF',
  background: '#F6F7FB',
  gradient: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 55%, #EC4899 100%)',
  softGradient: 'linear-gradient(180deg, #EEF2FF 0%, #F6F7FB 100%)',
};

const fontFamily = `'Plus Jakarta Sans', 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`;

let theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: brand.primary, light: brand.primaryLight, dark: brand.primaryDark, contrastText: '#FFFFFF' },
    secondary: { main: brand.pink, contrastText: '#FFFFFF' },
    success: { main: brand.green },
    warning: { main: '#F59E0B' },
    error: { main: '#EF4444' },
    text: { primary: brand.ink, secondary: brand.slate },
    background: { default: brand.background, paper: brand.surface },
    divider: brand.border,
  },
  // 4px unit: sx borderRadius 3 = 12px, 4 = 16px, 5 = 20px.
  shape: { borderRadius: 4 },
  typography: {
    fontFamily,
    h1: { fontWeight: 800, letterSpacing: '-0.03em' },
    h2: { fontWeight: 800, letterSpacing: '-0.025em' },
    h3: { fontWeight: 800, letterSpacing: '-0.02em' },
    h4: { fontWeight: 800, letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 700, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { fontWeight: 700, textTransform: 'none', letterSpacing: 0 },
  },
  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
  },
});

theme = createTheme(theme, {
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { WebkitTextSizeAdjust: '100%', scrollBehavior: 'smooth' },
        body: {
          backgroundColor: brand.background,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          WebkitTapHighlightColor: 'transparent',
          overflowX: 'hidden',
        },
        '::selection': { backgroundColor: alpha(brand.primary, 0.18) },
        // iOS Safari zooms into any focused input whose font-size is below 16px.
        '@media (max-width: 899.95px)': {
          'input, textarea, select, .ql-editor': { fontSize: '16px !important' },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 12,
          minHeight: 44,
          paddingInline: 18,
          transition: 'background-color .2s ease, box-shadow .2s ease, transform .2s ease, color .2s ease',
        },
        containedPrimary: {
          backgroundImage: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)',
          boxShadow: `0 8px 20px -8px ${alpha(brand.primary, 0.6)}`,
          '@media (hover: hover)': {
            '&:hover': { boxShadow: `0 12px 24px -8px ${alpha(brand.primary, 0.7)}`, transform: 'translateY(-1px)' },
          },
          '&.Mui-disabled': { backgroundImage: 'none' },
        },
        outlined: { borderWidth: 1.5, '&:hover': { borderWidth: 1.5 } },
        sizeSmall: { minHeight: 36, paddingInline: 12 },
        sizeLarge: { minHeight: 52, fontSize: '1.05rem', paddingInline: 26 },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { '@media (pointer: coarse)': { minWidth: 44, minHeight: 44 } } },
    },
    MuiPaper: {
      styleOverrides: { rounded: { borderRadius: 18 } },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          border: `1px solid ${brand.border}`,
          boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.08)',
        },
      },
    },
    MuiTextField: { defaultProps: { variant: 'outlined' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#FFFFFF',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: brand.border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: brand.primaryLight },
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, borderRadius: 10 } },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 20, margin: 16, width: 'calc(100% - 32px)' } },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: 16, border: `1px solid ${brand.border}`, boxShadow: '0 20px 45px -15px rgba(15,23,42,0.25)' },
      },
    },
    MuiTooltip: {
      defaultProps: { enterTouchDelay: 400, arrow: true },
      styleOverrides: { tooltip: { backgroundColor: brand.ink, fontSize: '0.8rem', borderRadius: 8 }, arrow: { color: brand.ink } },
    },
    MuiPaginationItem: {
      styleOverrides: { root: { fontWeight: 700, borderRadius: 10 } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 14 } },
    },
    MuiTab: {
      styleOverrides: { root: { textTransform: 'none', fontWeight: 700, minHeight: 48 } },
    },
    MuiAutocomplete: {
      styleOverrides: { paper: { borderRadius: 14, boxShadow: '0 20px 45px -15px rgba(15,23,42,0.25)' } },
    },
  },
});

theme = responsiveFontSizes(theme, { factor: 2.4 });

export default theme;
